import { inverseRate, roundDecimal, toFixedDecimal } from '@mms/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { Link } from 'react-router';
import { z } from 'zod';
import { ApiError, api } from '../api/client';
import type { Row } from '../api/types';
import { useCan } from '../auth/AuthContext';
import { alphaReq, currencyCodeReq, selectReq, toId, toText } from '../forms/zod';
import { useBalances } from '../ui/BalanceCard';
import type { Column } from '../ui/DataTable';
import { useConfirm, useToast } from '../ui/feedback';
import { Amount } from '../ui/format';
import { toOptions, useLatestRates, useLookup } from '../ui/lookups';
import { FieldRenderer, invalidateData, type CrudConfig, type FieldDef } from './CrudScreen';

/**
 * Currency master (redesigned 2026-10-16, migration 031): Symbol, Decimal places (0-4) and Active; today's rate against the base currency; safe actions.
 *   - Deactivate hides a currency from every dropdown and keeps its history. The base currency cannot be deactivated or deleted.
 *   - A currency with transactions keeps its Code and Opening balance (locked) and can only be deactivated.
 *   - Making another currency the base asks first and hands the flag over; switching off the only base is blocked.
 * Opening balances are stored with 2 decimals whatever the decimal places are: the decimals change how amounts are shown.
 */
const STORED_DP = 2;
const strip = (v: string) => v.replace(/,/g, '').trim();
/** A signed decimal string rounded half-up to `dp` decimals ("-1250" to 3 -> "-1250.000"); anything that is not a number is returned as typed. */
const fmtSigned = (v: string, dp: number): string => {
  const s = strip(v);
  const neg = s.startsWith('-');
  const r = roundDecimal(s.replace(/^-/, ''), dp);
  return r === null ? s : `${neg ? '-' : ''}${r}`;
};
const yes = (v: boolean) => (v ? <i className="bi bi-check-lg text-success" aria-label="Yes" /> : <span className="text-body-tertiary" aria-label="No">–</span>);
/** Thousands separators for display ("-1234.5" -> "-1,234.5"). */
const group = (v: string) => v.replace(/^(-?)(\d+)/, (_m, sign: string, int: string) => sign + int.replace(/\B(?=(\d{3})+(?!\d))/g, ','));
const DECIMAL_OPTIONS = [0, 1, 2, 3, 4].map((n) => ({ value: String(n), label: String(n) }));

const openingReq = z.string().trim().min(1, 'Enter the opening balance (0 if none)').regex(/^-?\d+(\.\d{1,4})?$/, 'Use a number');

const currencySchema = z.object({
  code: currencyCodeReq,
  name: alphaReq(),
  symbol: z.string().trim().max(10, 'Up to 10 characters'),
  countryId: selectReq('Select a country'),
  decimals: z.string(),
  openingBalance: openingReq,
  isBase: z.boolean(),
  isSupplier: z.boolean(),
  isActive: z.boolean(),
});

/** Why a currency cannot be deleted, or null when it can (the server enforces the same rules). */
export const deleteBlockedReason = (r: Row): string | null =>
  r.isBase ? 'The base currency cannot be deleted'
  : r.hasTransactions ? 'This currency has transactions and cannot be deleted. Deactivate it instead.'
  : r.usedByParties ? 'Agents, banks, sellers, dispatchers or customers still use this currency. Remove those first, or deactivate it.'
  : null;

/** "1 AED = 0.1048 OMR": the table holds units of the currency per 1 unit of the base, so the other way round is its inverse. */
const rateText = (code: string, rate1: string, baseCode: string) => {
  const inv = inverseRate(rate1);
  return inv ? `1 ${code} = ${toFixedDecimal(inv, 4)} ${baseCode}` : null;
};

/** Deactivate / Activate, kept out of the generic screen: a safe alternative to Delete that keeps the history. */
function useCurrencyActions() {
  const qc = useQueryClient();
  const toast = useToast();
  const canEdit = useCan('currencies', 'edit');
  const toggle = useMutation({
    mutationFn: (row: Row) => api.patch(`/currencies/${row.id}`, { isActive: !row.isActive }),
    onSuccess: async (_d, row) => {
      await invalidateData(qc);
      toast.success(row.isActive ? `${row.code} deactivated` : `${row.code} activated`);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Could not change the status'),
  });
  return (row: Row) => {
    if (!canEdit) return null;
    const blocked = row.isActive && row.isBase ? 'The base currency cannot be deactivated' : undefined;
    return (
      <span className="d-inline-block me-1" title={blocked}>
        <Button size="sm" variant="outline-secondary" disabled={!!blocked || toggle.isPending} onClick={() => toggle.mutate(row)}
          aria-label={`${row.isActive ? 'Deactivate' : 'Activate'} ${row.code}`}>
          <i className={`bi ${row.isActive ? 'bi-eye-slash' : 'bi-eye'} me-1`} aria-hidden />{row.isActive ? 'Deactivate' : 'Activate'}
        </Button>
      </span>
    );
  };
}

export const currenciesConfig: CrudConfig = {
  title: 'Currencies', singular: 'currency', icon: 'currency-exchange', menu: 'currencies', endpoint: '/currencies', update: 'patch',
  confirmDiscard: true,
  schema: currencySchema,
  defaults: () => ({ code: '', name: '', symbol: '', countryId: '', decimals: '2', openingBalance: '0.00', isBase: false, isSupplier: true, isActive: true }),
  toForm: (r) => ({
    code: r.code, name: r.name, symbol: r.symbol ?? '', countryId: r.countryId ? String(r.countryId) : '', decimals: String(r.decimals ?? 2),
    openingBalance: fmtSigned(String(r.openingBalance), Number(r.decimals ?? 2)),
    isBase: !!r.isBase, isSupplier: !!r.isSupplier, isActive: r.isActive !== false,
  }),
  toPayload: (v) => ({
    code: v.code.trim().toUpperCase(), name: v.name.trim(), symbol: toText(v.symbol), countryId: toId(v.countryId), decimals: Number(v.decimals),
    isBase: !!v.isBase, isSupplier: !!v.isSupplier, isActive: !!v.isActive,
    // the stored opening balance has 2 decimals
    openingBalance: fmtSigned(v.openingBalance, STORED_DP),
  }),
  rowLabel: (r) => `${r.code} – ${r.name}`,
  initialSort: { key: 'code', dir: 'asc' },
  deleteBlocked: deleteBlockedReason,
  useRowActions: useCurrencyActions,
  useInfo: () => (
    <>
      <strong>Cash in hand</strong> is only the physical cash in that currency (opening balance plus cash receipts, cash payments, cash expenses and cash fund transfers). Banks,
      dispatchers, agents and sellers are separate balances on their own screens and are not added here. <strong>Today&apos;s rate</strong> is the latest retail rate against the base currency.
    </>
  ),
  useTable: () => {
    const balances = useBalances('currencies');
    const rates = useLatestRates();
    const balanceById = new Map((balances.data ?? []).map((b) => [b.id, b]));
    const rateById = new Map((rates.data ?? []).map((r) => [r.currencyId, r]));
    const all = useLookup('currencies', { all: 1 });
    const baseCode = (all.data ?? []).find((c) => c.isBase)?.code as string | undefined;
    const columns: Column[] = [
      { key: 'countryName', header: 'Country', sortKey: 'country' },
      {
        key: 'code', header: 'Currency', sortKey: 'code', className: 'fw-semibold',
        render: (r) => <>{r.code}{r.symbol ? <small className="text-body-secondary fw-normal ms-2">{r.symbol}</small> : null}</>,
      },
      { key: 'name', header: 'Name', sortKey: 'name', hideOnMobile: true },
      { key: 'isBase', header: 'Base', align: 'center', render: (r) => yes(r.isBase) },
      { key: 'isSupplier', header: 'Dispatcher', align: 'center', render: (r) => yes(r.isSupplier) },
      {
        key: 'isActive', header: 'Status',
        render: (r) => <span className={`badge text-bg-${r.isActive ? 'success' : 'secondary'}`}>{r.isActive ? 'Active' : 'Inactive'}</span>,
      },
      {
        key: 'rate', header: "Today's rate", align: 'end', hideOnMobile: true,
        render: (r) => {
          if (r.isBase) return <span className="text-body-secondary">Base</span>;
          const x = rateById.get(r.id);
          const text = x && baseCode ? rateText(r.code, String(x.rate1), baseCode) : null;
          if (!text) return <span className="text-body-tertiary">—</span>;
          return <span data-testid={`rate-${r.code}`}>{text}<div className="small text-body-secondary">{x?.updatedAt ? dayjs(String(x.updatedAt)).format('DD-MM hh:mm A') : ''}</div></span>;
        },
      },
      {
        // Cash in hand only (2026-10-17): Cash, a Bank, a Dispatcher's account and the parties are separate balances, so nothing else is added in.
        key: 'cashInHand', header: 'Cash in hand', align: 'end', className: 'fw-semibold',
        render: (r) => {
          if (balances.isPending) return <span className="placeholder-glow"><span className="placeholder col-12" style={{ width: 70 }} /></span>;
          const b = balanceById.get(r.id);
          const dp = Number(r.decimals ?? 2);
          return <Amount value={b?.balance ?? '0'} code={r.code} decimals={dp} />;
        },
      },
    ];
    return {
      columns,
      filters: [
        { param: 'isActive', label: 'Status', allLabel: 'All statuses', options: [{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }] },
        { param: 'isSupplier', label: 'Dispatcher currencies only', type: 'checkbox' as const, value: 'true', options: [] },
      ],
    };
  },
  useForm: (form, { mode, row, readOnly }) => {
    const confirm = useConfirm();
    const countries = useLookup('countries');
    const all = useLookup('currencies', { all: 1 });
    const rates = useLatestRates();
    const [baseNotice, setBaseNotice] = useState<string | null>(null);

    const code = String(form.watch('code') ?? '');
    const decimals = String(form.watch('decimals') ?? '2');
    const isBase = !!form.watch('isBase');
    const isSupplier = !!form.watch('isSupplier');
    const isActive = !!form.watch('isActive');
    const locked = mode === 'edit' && !!row?.hasTransactions;
    const currentBase = (all.data ?? []).find((c) => c.isBase && c.id !== row?.id);
    const baseCode = ((all.data ?? []).find((c) => c.isBase)?.code as string | undefined) ?? undefined;

    // the code is always upper case
    useEffect(() => {
      if (code !== code.toUpperCase()) form.setValue('code', code.toUpperCase(), { shouldDirty: true });
    }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

    // changing the decimal places reformats the opening balance straight away
    const lastDecimals = useRef(decimals);
    useEffect(() => {
      if (lastDecimals.current === decimals) return;
      lastDecimals.current = decimals;
      const raw = String(form.getValues('openingBalance') ?? '');
      if (roundDecimal(strip(raw).replace(/^-/, ''), 0) !== null) form.setValue('openingBalance', fmtSigned(raw, Number(decimals)), { shouldDirty: true });
    }, [decimals]); // eslint-disable-line react-hooks/exhaustive-deps

    const onBase = async (checked: boolean) => {
      setBaseNotice(null);
      if (checked) {
        if (currentBase && !(await confirm({ title: 'Change the base currency', message: `${currentBase.code} will stop being the base currency. Continue?`, confirmLabel: 'Continue', danger: false }))) return;
        form.setValue('isBase', true, { shouldDirty: true });
        form.setValue('isActive', true, { shouldDirty: true }); // the base currency is always active
      } else if (row?.isBase) {
        setBaseNotice('This is the base currency. Make another currency the base instead.');
      } else {
        form.setValue('isBase', false, { shouldDirty: true });
      }
    };

    const thisRate = row ? (rates.data ?? []).find((r) => r.currencyId === row.id) : undefined;
    const rateLine = mode === 'create' ? 'Set after the currency is saved'
      : row?.isBase || isBase ? 'This is the base currency'
      : thisRate && baseCode ? rateText(String(row?.code), String(thisRate.rate1), baseCode) ?? 'No rate yet' : 'No rate yet';

    const field = (def: FieldDef, col: number) => <FieldRenderer form={form} field={{ ...def, col }} readOnly={readOnly} />;
    const lockNote = 'Locked: this currency has transactions';

    const body = (
      <>
        <div className="row">
          {field({ name: 'code', label: 'Code', type: 'text', required: true, placeholder: 'OMR', disabled: locked, help: locked ? lockNote : '3 letters, for example OMR' }, 4)}
          {field({ name: 'name', label: 'Name', type: 'text', required: true }, 4)}
          {field({ name: 'symbol', label: 'Symbol', type: 'text', help: 'Shown on receipts and printed invoices' }, 4)}
        </div>
        <div className="row">
          {field({ name: 'countryId', label: 'Country', type: 'select', required: true, options: toOptions(countries.data), placeholder: 'Select…' }, 4)}
          {field({ name: 'decimals', label: 'Decimal places', type: 'select', required: true, options: DECIMAL_OPTIONS, help: 'Used for every amount in this currency (OMR 3, AED 2)' }, 4)}
          {field({
            name: 'openingBalance', label: 'Opening balance (cash)', type: 'money', required: true, disabled: locked,
            help: locked ? lockNote : `Cash in hand on day one, in this currency. Enter 0 if none${Number(decimals) > STORED_DP ? '. Stored to 2 decimals.' : ''}`,
          }, 4)}
        </div>
        <div className="row mb-3">
          <div className="col-12 col-md-4">
            <Form.Check type="checkbox" id="currency-base" label="Base currency" checked={isBase} disabled={readOnly} onChange={(e) => onBase(e.target.checked)} />
            <Form.Text muted>{currentBase ? `Current base: ${currentBase.code}. Turning this on replaces it` : row?.isBase ? 'This is the base currency' : 'No base currency yet'}</Form.Text>
            {baseNotice && <div className="text-danger small mt-1" role="alert">{baseNotice}</div>}
          </div>
          <div className="col-12 col-md-4">
            <Form.Check type="checkbox" id="currency-supplier" label="Dispatcher currency" checked={isSupplier} disabled={readOnly}
              onChange={(e) => form.setValue('isSupplier', e.target.checked, { shouldDirty: true })} />
            <Form.Text muted>Can be used for dispatchers and in the dispatcher dropdowns</Form.Text>
          </div>
          <div className="col-12 col-md-4">
            <Form.Check type="checkbox" id="currency-active" label="Active" checked={isActive} disabled={readOnly || isBase}
              onChange={(e) => form.setValue('isActive', e.target.checked, { shouldDirty: true })} />
            <Form.Text muted>{isBase ? 'The base currency cannot be deactivated' : 'Inactive currencies are hidden from every dropdown'}</Form.Text>
          </div>
        </div>
        <div className="rounded bg-body-tertiary px-3 py-2 d-flex flex-wrap justify-content-between align-items-center gap-2" data-testid="rate-panel">
          <span><small className="me-2">Today&apos;s retail rate</small><strong>{rateLine}</strong></span>
          <Link to="/masters/exchange-rates" className="btn btn-outline-secondary btn-sm">Open Exchange rates</Link>
        </div>
      </>
    );
    return { body, fields: [] };
  },
};
